import { useEffect, useState, type DependencyList } from 'react';

interface AsyncState<T> {
  data?: T;
  loading: boolean;
  error?: string;
}

/**
 * Run an async loader whenever `deps` change, tracking loading/error state and
 * exposing `reload()` to re-run on demand (e.g. after a mutation).
 */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList) {
  const [state, setState] = useState<AsyncState<T>>({ loading: true });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;
    setState((s) => ({ data: s.data, loading: true }));
    fn()
      .then((d) => active && setState({ data: d, loading: false }))
      .catch((e) => active && setState({ loading: false, error: (e as Error).message }));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}
